/*
 * The object menu's arithmetic (คลิกขวา / แตะสองนิ้ว, src/ui/contextMenu.ts), kept free of DOM/Fabric for the tests
 * (tests/context-menu.test.ts, tests/groups.test.ts): where an aligned or evenly spread object goes, where a layer move
 * puts it in the stack, which style is copied, the main colour of a picture, the page box that fits a selection, and
 * which objects belong to one group (จัดกลุ่ม).
 */

export interface Box { x: number; y: number; w: number; h: number }

export type Align = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export const ALIGN_LABEL: Record<Align, string> = {
  left: 'ชิดซ้าย', hcenter: 'กึ่งกลางแนวนอน', right: 'ชิดขวา', top: 'ชิดบน', vcenter: 'กึ่งกลางแนวตั้ง', bottom: 'ชิดล่าง'
};

/** how far `box` moves so its side/centre lines up with `target`'s */
export function alignShift(box: Box, target: Box, how: Align): { dx: number; dy: number } {
  switch (how) {
    case 'left': return { dx: target.x - box.x, dy: 0 };
    case 'hcenter': return { dx: target.x + target.w / 2 - (box.x + box.w / 2), dy: 0 };
    case 'right': return { dx: target.x + target.w - (box.x + box.w), dy: 0 };
    case 'top': return { dx: 0, dy: target.y - box.y };
    case 'vcenter': return { dx: 0, dy: target.y + target.h / 2 - (box.y + box.h / 2) };
    case 'bottom': return { dx: 0, dy: target.y + target.h - (box.y + box.h) };
  }
}

/** the box around several boxes */
export function unionBox(boxes: Box[]): Box {
  const x = Math.min(...boxes.map(b => b.x)), y = Math.min(...boxes.map(b => b.y));
  return { x, y, w: Math.max(...boxes.map(b => b.x + b.w)) - x, h: Math.max(...boxes.map(b => b.y + b.h)) - y };
}

export type LayerMove = 'front' | 'forward' | 'backward' | 'back';
export const LAYER_LABEL: Record<LayerMove, string> = { front: 'ไปหน้าสุด', forward: 'ขึ้น 1 ชั้น', backward: 'ลง 1 ชั้น', back: 'ไปหลังสุด' };

/**
 * The new place of the object at `from` in the canvas stack. Index 0 is the artboard; locked backgrounds directly
 * above it (เป็นพื้นหลัง) stay at the bottom, so "ไปหลังสุด" stops just above them (it would vanish under one).
 * Returns `from` when the object cannot go further, with a Thai reason.
 */
export function layerIndex(stack: Array<{ board?: boolean; locked?: boolean }>, from: number, how: LayerMove): { to: number; why?: string } {
  let floor = 1;
  while (floor < stack.length && floor !== from && stack[floor].locked) floor++;
  const top = stack.length - 1;
  const to = how === 'front' ? top : how === 'forward' ? Math.min(top, from + 1)
    : how === 'backward' ? Math.max(floor, from - 1) : floor;
  if (to === from) {
    return { to, why: how === 'front' || how === 'forward' ? 'ชิ้นนี้อยู่บนสุดแล้ว'
      : floor > 1 ? 'ชิ้นนี้อยู่ล่างสุดแล้ว (ต่ำกว่านี้คือพื้นหลังที่ล็อกไว้ ปลดล็อกได้ที่ "เลเยอร์")' : 'ชิ้นนี้อยู่ล่างสุดแล้ว' };
  }
  return { to };
}

/* ---------- คัดลอกรูปแบบ ---------- */

export type StyleKind = 'text' | 'shape' | 'line' | 'image';
export const STYLE_KIND_LABEL: Record<StyleKind, string> = { text: 'ข้อความ', shape: 'รูปทรง', line: 'เส้น', image: 'รูปภาพ' };
const PROPS: Record<StyleKind, string[]> = {
  text: ['fontFamily', 'fontWeight', 'fontStyle', 'fontSize', 'fill', 'charSpacing', 'lineHeight', 'textAlign', 'underline', 'stroke', 'strokeWidth', 'paintFirst', 'opacity', 'shadow', 'textFx'],
  shape: ['fill', 'stroke', 'strokeWidth', 'strokeDashArray', 'opacity', 'shadow'],
  line: ['stroke', 'strokeWidth', 'strokeLineCap', 'strokeDashArray', 'opacity', 'shadow'],
  image: ['opacity', 'photoEdit', 'shadow']
};

export function styleKind(type: string | undefined): StyleKind | null {
  if (type === 'thaiTextbox' || type === 'textbox' || type === 'i-text' || type === 'text') return 'text';
  if (type === 'rect' || type === 'circle' || type === 'triangle' || type === 'ellipse' || type === 'polygon' || type === 'path') return 'shape';
  if (type === 'line') return 'line';
  if (type === 'image') return 'image';
  return null;
}

export interface CopiedStyle { kind: StyleKind; props: Record<string, unknown> }

/** the look of an object (plain data: fill, font, outline…), or null for a kind that has none to copy */
export function styleOf(o: Record<string, unknown>): CopiedStyle | null {
  const kind = styleKind(o.type as string | undefined);
  if (!kind) return null;
  const props: Record<string, unknown> = {};
  for (const k of PROPS[kind]) if (o[k] !== undefined) props[k] = structuredClone(o[k]);
  // เอฟเฟกต์ข้อความ (src/editor/textFx.ts): a text without one pastes "no effect" — the target's own effect, outline
  // and shadow must not stay on (they are written as none, not left out)
  if (kind === 'text') {
    props.textFx ??= null; props.stroke ??= null; props.shadow ??= null; props.paintFirst ??= 'fill';
  }
  return { kind, props };
}

/** why a copied style cannot go on an object of `type`, or null */
export function styleProblem(s: CopiedStyle, type: string | undefined): string | null {
  const kind = styleKind(type);
  if (!kind) return 'ชิ้นนี้ไม่มีรูปแบบให้วาง';
  if (kind !== s.kind) return `รูปแบบที่คัดลอกมาเป็นของ${STYLE_KIND_LABEL[s.kind]} ใช้กับ${STYLE_KIND_LABEL[kind]}ไม่ได้`;
  return null;
}

/* ---------- ใช้สีกับพื้นหลัง ---------- */

const hex2 = (n: number) => Math.round(n).toString(16).padStart(2, '0');

/**
 * The main colour of a picture's pixels (RGBA): the most common colour (16 levels per channel), averaged inside
 * that group; see-through pixels are left out. Null when nothing is solid enough to pick from.
 */
export function mainColor(rgba: Uint8ClampedArray | number[]): string | null {
  const groups = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    if (rgba[i + 3] < 128) continue;
    const key = (rgba[i] >> 4) << 8 | (rgba[i + 1] >> 4) << 4 | (rgba[i + 2] >> 4);
    const g = groups.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    g.n++; g.r += rgba[i]; g.g += rgba[i + 1]; g.b += rgba[i + 2];
    groups.set(key, g);
  }
  let best: { n: number; r: number; g: number; b: number } | null = null;
  for (const g of groups.values()) if (!best || g.n > best.n) best = g;
  return best ? `#${hex2(best.r / best.n)}${hex2(best.g / best.n)}${hex2(best.b / best.n)}` : null;
}

/** a CSS colour as #rrggbb when it is a plain one (#rgb, #rrggbb, rgb()/rgba() solid), else null (gradients, see-through) */
export function plainHex(c: unknown): string | null {
  if (typeof c !== 'string') return null;
  const s = c.trim().toLowerCase();
  let m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (m) return `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`;
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(s);
  if (m && (m[4] === undefined || Number(m[4]) >= 1)) return `#${hex2(+m[1])}${hex2(+m[2])}${hex2(+m[3])}`;
  return null;
}

/* ---------- กระจายระยะห่างเท่ากัน ---------- */

export type Axis = 'h' | 'v';
export const DISTRIBUTE_LABEL: Record<Axis, string> = { h: 'กระจายระยะห่างแนวนอนเท่ากัน', v: 'กระจายระยะห่างแนวตั้งเท่ากัน' };

/**
 * How far each box moves (same order as given) so the gaps between them along `axis` are equal: the first one
 * (left-/top-most) and the far edge of the whole row stay where they are; the others keep their order.
 */
export function distributeShifts(boxes: Box[], axis: Axis): Array<{ dx: number; dy: number }> {
  const start = (b: Box) => axis === 'h' ? b.x : b.y;
  const size = (b: Box) => axis === 'h' ? b.w : b.h;
  const order = boxes.map((_, k) => k).sort((a, b) => start(boxes[a]) - start(boxes[b]) || a - b);
  const from = start(boxes[order[0]]);
  const to = Math.max(...boxes.map(b => start(b) + size(b)));
  const gap = (to - from - boxes.reduce((n, b) => n + size(b), 0)) / Math.max(1, boxes.length - 1);
  const out = boxes.map(() => ({ dx: 0, dy: 0 }));
  let at = from;
  for (const k of order) {
    const d = at - start(boxes[k]);
    out[k] = axis === 'h' ? { dx: d, dy: 0 } : { dx: 0, dy: d };
    at += size(boxes[k]) + gap;
  }
  return out;
}

/* ---------- จัดกลุ่ม (choice ก: every object stays its own, members share a `groupId`) ---------- */

export interface Member { groupId?: unknown }

/** the object's group, or null */
export const groupOf = (o: Member): string | null => typeof o.groupId === 'string' && o.groupId ? o.groupId : null;

/** a new group id (short, unique enough inside one design) */
export const newGroupId = () => 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

/**
 * The chosen objects plus every other member of their groups, in the order of `all` (the objects that can be chosen).
 * `dropped`: objects just taken out of the selection (Shift-click) — their whole group leaves with them.
 */
export function withMates<T extends Member>(chosen: T[], all: T[], dropped: T[] = []): T[] {
  const gone = new Set(dropped.map(groupOf).filter((g): g is string => !!g));
  const keep = chosen.filter(o => { const g = groupOf(o); return !g || !gone.has(g); });
  const ids = new Set(keep.map(groupOf).filter((g): g is string => !!g));
  const out = all.filter(o => keep.includes(o) || (groupOf(o) !== null && ids.has(groupOf(o)!)));
  return out.concat(keep.filter(o => !all.includes(o)));
}

/** the group when `chosen` is exactly one whole group (every member of it in `all` is chosen), else null */
export function wholeGroup<T extends Member>(chosen: T[], all: T[]): string | null {
  const g = chosen.length > 1 ? groupOf(chosen[0]) : null;
  if (!g || chosen.some(o => groupOf(o) !== g)) return null;
  return all.every(o => groupOf(o) !== g || chosen.includes(o)) ? g : null;
}

/**
 * Who gets the new id of จัดกลุ่ม: the chosen objects plus every member of a group among them, hidden and locked
 * members too (they cannot be chosen, but must not be left behind in the old group — BUG-44). Order of `all`.
 */
export function groupTargets<T extends Member>(chosen: T[], all: T[]): T[] {
  const ids = new Set(chosen.map(groupOf).filter((g): g is string => !!g));
  return all.filter(o => chosen.includes(o) || (groupOf(o) !== null && ids.has(groupOf(o)!))).concat(chosen.filter(o => !all.includes(o)));
}

/** groups that have fewer than 2 members in `all` (after a delete): those members are no longer in a group */
export function loneMembers<T extends Member>(all: T[]): T[] {
  const n = new Map<string, number>();
  for (const o of all) { const g = groupOf(o); if (g) n.set(g, (n.get(g) ?? 0) + 1); }
  return all.filter(o => { const g = groupOf(o); return !!g && (n.get(g) ?? 0) < 2; });
}

/** the chosen objects as the pieces that move as one: each group (2+ members chosen) is one piece, the rest one each */
export function units<T extends Member>(list: T[]): T[][] {
  const out: T[][] = [], at = new Map<string, T[]>();
  for (const o of list) {
    const g = groupOf(o);
    if (!g) { out.push([o]); continue; }
    let u = at.get(g);
    if (!u) { u = []; at.set(g, u); out.push(u); }
    u.push(o);
  }
  return out;
}

/**
 * New group ids for copies (วาง / ทำสำเนา): copies never join the original's group; a group copied with 2+ members
 * becomes a new group of its own, a lone member copied is no longer in a group. Same order as given.
 */
export function regroupIds(ids: Array<string | null>, make: () => string = newGroupId): Array<string | null> {
  const count = new Map<string, number>(), fresh = new Map<string, string>();
  for (const g of ids) if (g) count.set(g, (count.get(g) ?? 0) + 1);
  return ids.map(g => {
    if (!g || (count.get(g) ?? 0) < 2) return null;
    if (!fresh.has(g)) fresh.set(g, make());
    return fresh.get(g)!;
  });
}

/* ---------- ปรับขนาดหน้าให้พอดีกับที่เลือก ---------- */

/** the page box around a selection: whole pixels (out to the next one), at least `min` per side */
export function fitPageBox(b: Box, min = 16): Box {
  const x = Math.floor(b.x), y = Math.floor(b.y);
  let w = Math.ceil(b.x + b.w) - x, h = Math.ceil(b.y + b.h) - y;
  w = Math.max(min, w); h = Math.max(min, h);
  return { x, y, w, h };
}
